import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { KeyRound, Mail, Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import apiClient from "../lib/apiClient"; 

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(1); // 1 = Request Email, 2 = Verify OTP & Reset
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await apiClient.post("/forgot-password", { email });
      toast.success(res.data.message);
      if (res.data.dev_otp) toast.info(`TEST OTP: ${res.data.dev_otp}`, { duration: 10000 });
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await apiClient.post("/reset-password", { email, otp, password: newPassword });
      toast.success(res.data.message);
      navigate("/login"); // Reset success ke baad wapas login bhejenge
    } catch (err) {
      toast.error(err.response?.data?.message || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-2xl border-0 bg-white rounded-2xl">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-sky-100 p-3 rounded-2xl mb-4">
            {step === 1 ? <KeyRound className="w-8 h-8 text-sky-600" /> : <ShieldCheck className="w-8 h-8 text-emerald-600" />}
          </div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">
            {step === 1 ? "Forgot Password" : "Set New Password"}
          </h1>
          <p className="text-slate-500 text-sm font-medium mt-1 text-center">
            {step === 1 ? "Enter your registered email to receive a reset code" : `Enter the 6-digit code sent to ${email}`}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleRequestOtp} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Email Address</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input type="email" placeholder="registered@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-10 font-medium bg-slate-50 border-slate-200" required />
              </div>
            </div>
            
            <Button type="submit" disabled={loading} className="w-full h-12 bg-sky-600 hover:bg-sky-500 text-white font-bold shadow-lg">
              {loading ? "Sending..." : "Send Reset Code"}
            </Button>

            <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm font-bold text-slate-500 hover:text-slate-800 mt-6">
              <ArrowLeft className="w-4 h-4" /> Back to Login
            </Link>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Reset OTP</Label>
              <Input type="text" maxLength="6" placeholder="000000" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} className="h-12 text-center text-xl tracking-[0.5em] font-black bg-slate-50 border-slate-200" required />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest">New Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input type="password" placeholder="Min 6 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="pl-10 h-10 font-medium bg-slate-50 border-slate-200" required minLength="6" />
              </div>
            </div>

            <Button type="submit" disabled={loading || otp.length !== 6} className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-lg mt-2">
              {loading ? "Updating..." : "Update Password"}
            </Button>
            
            <Button type="button" variant="ghost" onClick={() => setStep(1)} className="w-full text-slate-500 hover:text-slate-800 text-xs font-bold mt-2">
              Change Email Address
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}